import React, { useState, useEffect } from 'react';
import { Save, X, User, AlertCircle, Shield, Check } from 'lucide-react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const UserForm = ({ user, editingUser, onUserCreated, onUserUpdated, onCancel, showSuccess, showError, showWarning }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [userForm, setUserForm] = useState({
    username: '',
    firstName: '',
    lastName: '',
    department: '',
    costCenter: '',
    plant: '',
    purchasingOrg: '',
    purchasingGroup: '',
    sapUser: '',
    requestor: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'purchaser',
    isActive: true
  });
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Berechtigungsprofile
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [assignedProfiles, setAssignedProfiles] = useState([]);
  const [profilesLoading, setProfilesLoading] = useState(false);

  // Berechtigungsprofile laden
  const loadProfiles = async () => {
    try {
      const response = await fetch('/api/permission-profiles', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (response.ok) {
        const profiles = await response.json();
        setAvailableProfiles(profiles);
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    }
  };

  // Profile eines Users laden
  const loadUserProfiles = async (userId) => {
    try {
      setProfilesLoading(true);
      const response = await fetch(`/api/permission-profiles/user/${userId}/profiles`, {
        headers: { 'Authorization': `Bearer ${user.token}` }
      });
      if (response.ok) {
        const profiles = await response.json();
        setAssignedProfiles(profiles.map(p => p.id));
      }
    } catch (error) {
      console.error('Error loading user profiles:', error);
    } finally {
      setProfilesLoading(false);
    }
  };

  useEffect(() => {
    loadProfiles();
  }, [user.token]);

  useEffect(() => {
    if (editingUser?.id) {
      setIsEditing(true);
      setUserForm({
        username: editingUser.username || '',
        firstName: editingUser.first_name || '',
        lastName: editingUser.last_name || '',
        department: editingUser.department || '',
        costCenter: editingUser.cost_center || '',
        plant: editingUser.plant || '',
        purchasingOrg: editingUser.purchasing_org || '',
        purchasingGroup: editingUser.purchasing_group || '',
        sapUser: editingUser.sap_user || '',
        requestor: editingUser.requestor || '',
        email: editingUser.email || '',
        password: '',
        confirmPassword: '',
        role: editingUser.role || 'purchaser',
        isActive: editingUser.is_active !== undefined ? editingUser.is_active : true
      });
      loadUserProfiles(editingUser.id);
    }
  }, [editingUser]);

  const validateForm = () => {
    if (!userForm.email) {
      setUserError(t('admin.userForm.emailRequired'));
      return false;
    }

    if (!isEditing && !userForm.username) {
      setUserError(t('admin.userForm.usernameRequired'));
      return false;
    }

    if (!isEditing || userForm.password) {
      if (userForm.password !== userForm.confirmPassword) {
        setUserError(t('admin.userForm.passwordMismatch'));
        return false;
      }

      if (userForm.password && userForm.password.length < 6) {
        setUserError(t('admin.userForm.passwordMinLength'));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setUserLoading(true);
    setUserError('');

    try {
      const endpoint = isEditing 
        ? `/api/admin/users/${editingUser.id}`
        : '/api/admin/users';

      const method = isEditing ? 'PUT' : 'POST';

      const payload = {
        username: userForm.username,
        email: userForm.email,
        firstName: userForm.firstName,
        lastName: userForm.lastName,
        department: userForm.department,
        cost_center: userForm.costCenter,
        plant: userForm.plant,
        purchasing_org: userForm.purchasingOrg,
        purchasing_group: userForm.purchasingGroup,
        sap_user: userForm.sapUser,
        requestor: userForm.requestor,
        role: userForm.role,
        is_active: userForm.isActive
      };

      if (userForm.password) {
        payload.password = userForm.password;
      }

      const response = await fetch(endpoint, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || (isEditing ? t('admin.userForm.updateError') : t('admin.userForm.createError')));
      }

      // Profile zuweisen (bei Edit)
      if (isEditing && assignedProfiles.length >= 0) {
        try {
          await fetch(`/api/permission-profiles/user/${editingUser.id}/profiles`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${user.token}`
            },
            body: JSON.stringify({ profile_ids: assignedProfiles })
          });
        } catch (profileError) {
          console.error('Error saving profiles:', profileError);
        }
      }

      if (showSuccess) {
        showSuccess(isEditing ? t('admin.userForm.updated') : t('admin.userForm.created'));
      } else {
        alert(isEditing ? t('admin.userForm.updated') : t('admin.userForm.created'));
      }
      
      // Check if the current logged-in user was edited and update their context
      if (isEditing && editingUser.id === user.id && onUserUpdated) {
        console.log('Current user was updated, refreshing user context...');
        const updatedUserData = {
          ...user,
          username: userForm.username,
          firstName: userForm.firstName,
          lastName: userForm.lastName,
          department: userForm.department,
          costCenter: userForm.costCenter,
          plant: userForm.plant,
          purchasingOrg: userForm.purchasingOrg,
          purchasingGroup: userForm.purchasingGroup,
          sapUser: userForm.sapUser,
          requestor: userForm.requestor,
          email: userForm.email,
          role: userForm.role
        };
        onUserUpdated(updatedUserData);
      }
      
      onUserCreated();
      
    } catch (error) {
      console.error('User operation error:', error);
      setUserError(error.message);
    } finally {
      setUserLoading(false);
    }
  };

  return (
    <div className="relative group">
      {/* Form Background */}
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl'
        : 'glass-light-card border border-slate-200'
      } rounded-3xl ${isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'shadow-sm'}`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      } rounded-3xl`}></div>
      
      
      <div className="relative p-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            {/* User Icon */}
            <div className="relative group/icon">
              <div className={`${isDarkMode ? '' : 'hidden'} absolute inset-0 bg-gradient-to-br from-white/20 via-white/10 to-transparent rounded-2xl blur-sm`}></div>
              <div className={`relative w-12 h-12 rounded-2xl flex items-center justify-center border ${isDarkMode ? 'bg-gradient-to-br from-blue-600/60 via-blue-700/50 to-blue-800/60 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.3),inset_0_1px_0_rgba(255,255,255,0.3)] border-white/20' : 'bg-brand/10 border-brand/20'}`}>
                <User className={`w-6 h-6 ${isDarkMode ? 'text-white/90 drop-shadow-lg' : 'text-brand'}`} />
                <div className={`${isDarkMode ? '' : 'hidden'} absolute top-1 left-1 w-3 h-3 bg-white/30 rounded-full blur-sm`}></div>
              </div>
            </div>
            <div>
              <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>
                {isEditing ? t('admin.userForm.editTitle') : t('admin.userForm.createTitle')}
              </h2>
              <p className={`mt-1 ${isDarkMode ? 'text-white/70 drop-shadow-sm' : 'text-slate-600'}`}>
                {isEditing ? t('admin.userForm.editSubtitle') : t('admin.userForm.createSubtitle')}
              </p>
            </div>
          </div>
          
          {/* Close Button */}
          <button
            onClick={onCancel}
            disabled={userLoading}
            className={`relative group/close overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:transform-none disabled:opacity-50 ${isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 border border-slate-200'}`}
            style={isDarkMode ? {
              background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
              backdropFilter: 'blur(20px)',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            } : {}}
          >
            {/* Smooth Hover Overlay */}
            <div
              className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/close:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
              } : {}}
            ></div>

            <div className={`relative z-10 p-2 flex items-center justify-center transition-all duration-300 ${isDarkMode ? 'text-white drop-shadow-lg group-hover/close:drop-shadow-xl' : 'text-slate-700'}`}>
              <X className="w-5 h-5 transition-transform duration-300 group-hover/close:scale-110" />
            </div>

            {/* Smooth Wave Effect */}
            <div className={`absolute inset-0 opacity-0 group-hover/close:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
              <div
                className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/close:translate-x-full transition-transform duration-800"
                style={isDarkMode ? {
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                } : {}}
              ></div>
            </div>
          </button>
        </div>
        
        {/* Error Message */}
        {userError && (
          <div className="relative group/error mb-6">
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-br from-red-600/20 via-red-700/20 to-red-800/20 border border-red-400/30 backdrop-blur-xl'
              : 'bg-red-50 border border-red-200'
            } rounded-2xl`}></div>
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
              : ''
            } rounded-2xl`}></div>

            <div className="relative p-4">
              <div className="flex items-center space-x-3">
                <AlertCircle className={`w-5 h-5 ${isDarkMode ? 'text-red-300 drop-shadow-lg' : 'text-red-600'}`} />
                <p className={`text-sm font-medium ${isDarkMode ? 'text-red-200 drop-shadow-sm' : 'text-red-700'}`}>{userError}</p>
              </div>
            </div>
          </div>
        )}
      
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>
                {t('admin.userForm.username')}
              </label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.username}
                  onChange={(e) => setUserForm({...userForm, username: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="max.mustermann"
                  disabled={userLoading || isEditing}
                />
              </div>
              {isEditing && (
                <p className={`text-xs mt-1 ${isDarkMode ? 'text-blue-200 drop-shadow-sm' : 'text-brand'}`}>{t('admin.userForm.usernameReadonly')}</p>
              )}
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.email')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="email"
                  value={userForm.email}
                  onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="max.mustermann@firma.de"
                  disabled={userLoading}
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.firstName')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.firstName}
                  onChange={(e) => setUserForm({...userForm, firstName: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="Max"
                  disabled={userLoading}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.lastName')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.lastName}
                  onChange={(e) => setUserForm({...userForm, lastName: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="Mustermann"
                  disabled={userLoading}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.department')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.department}
                  onChange={(e) => setUserForm({...userForm, department: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="IT"
                  disabled={userLoading}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.costCenter')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.costCenter}
                  onChange={(e) => setUserForm({...userForm, costCenter: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="110700"
                  disabled={userLoading}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.plant')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.plant}
                  onChange={(e) => setUserForm({...userForm, plant: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="1000"
                  disabled={userLoading}
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.purchasingOrg')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.purchasingOrg}
                  onChange={(e) => setUserForm({...userForm, purchasingOrg: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="z.B. 1000"
                  disabled={userLoading}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.sapUser')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.sapUser}
                  onChange={(e) => setUserForm({...userForm, sapUser: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="z.B. SAP123456"
                  disabled={userLoading}
                />
              </div>
            </div>
          </div>
        
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.purchasingGroup')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.purchasingGroup}
                  onChange={(e) => setUserForm({...userForm, purchasingGroup: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="z.B. 001"
                  disabled={userLoading}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.requester')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <input
                  type="text"
                  value={userForm.requestor}
                  onChange={(e) => setUserForm({...userForm, requestor: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                  placeholder="z.B. Max Mustermann"
                  disabled={userLoading}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.role')}</label>
              <div className="relative">
                <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                <select
                  value={userForm.role}
                  onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                  className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white focus:ring-2 focus:ring-blue-400/50' : 'text-slate-900 focus:ring-2 focus:ring-brand/40'}`}
                  disabled={userLoading}
                >
                  <option value="purchaser" className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>{t('admin.userForm.rolePurchaser')}</option>
                  <option value="supervisor" className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>{t('admin.userForm.roleSupervisor')}</option>
                  <option value="admin" className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}`}>{t('admin.userForm.roleAdmin')}</option>
                </select>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={userForm.isActive}
                  onChange={(e) => setUserForm({...userForm, isActive: e.target.checked})}
                  className="rounded border-white/30 bg-transparent text-blue-500 focus:ring-blue-400/50 focus:ring-offset-0"
                  disabled={userLoading}
                />
                <span className={`text-sm ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'}`}>{t('admin.userForm.isActive')}</span>
              </label>
            </div>
          </div>

          {/* Passwort Sektion */}
          <div className="relative group/password">
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-br from-purple-600/10 via-indigo-600/10 to-purple-700/10 border border-purple-400/20 backdrop-blur-xl'
              : 'bg-slate-50 border border-slate-200'
            } rounded-2xl`}></div>
            <div className={`absolute inset-0 ${isDarkMode
              ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
              : ''
            } rounded-2xl`}></div>
            
            <div className="relative p-6">
              <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>
                {isEditing ? t('admin.userForm.changePassword') : t('admin.userForm.setPassword')}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>
                    {isEditing ? t('admin.userForm.newPassword') : t('admin.userForm.passwordRequired')}
                  </label>
                  <div className="relative">
                    <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                    <input
                      type="password"
                      value={userForm.password}
                      onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                      className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                      placeholder={t('admin.userForm.passwordMinLength')}
                      disabled={userLoading}
                      required={!isEditing}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90 drop-shadow-sm' : 'text-slate-700'} mb-2`}>{t('admin.userForm.confirmPassword')}</label>
                  <div className="relative">
                    <div className={`absolute inset-0 ${isDarkMode
                  ? 'bg-gradient-to-r from-gray-800/30 via-gray-700/30 to-gray-800/30 border border-white/20 backdrop-blur-xl'
                  : 'bg-white border border-slate-200'
                } rounded-2xl`}></div>
                    <input
                      type="password"
                      value={userForm.confirmPassword}
                      onChange={(e) => setUserForm({...userForm, confirmPassword: e.target.value})}
                      className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:outline-none transition-all duration-300 ${isDarkMode
                    ? 'text-white placeholder-white/50 focus:ring-2 focus:ring-blue-400/50'
                    : 'text-slate-900 placeholder-slate-400 focus:ring-2 focus:ring-brand/40'
                  }`}
                      placeholder={t('admin.userForm.confirmPassword')}
                      disabled={userLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Berechtigungsprofile Sektion (nur beim Bearbeiten) */}
          {isEditing && availableProfiles.length > 0 && (
            <div className="relative group/profiles">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-br from-indigo-600/10 via-blue-600/10 to-indigo-700/10 border border-indigo-400/20 backdrop-blur-xl'
                : 'bg-slate-50 border border-slate-200'
              } rounded-2xl`}></div>
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
                : ''
              } rounded-2xl`}></div>

              <div className="relative p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isDarkMode ? 'bg-indigo-500/20' : 'bg-brand/10'
                  }`}>
                    <Shield className={`w-4 h-4 ${isDarkMode ? 'text-indigo-400' : 'text-brand'}`} />
                  </div>
                  <div>
                    <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>
                      {t('admin.userForm.permissionProfiles')}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-white/60' : 'text-slate-600'}`}>
                      {t('admin.userForm.profilesHint')}
                    </p>
                  </div>
                </div>

                {profilesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-500 border-t-transparent"></div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {availableProfiles.map(profile => {
                      const isAssigned = assignedProfiles.includes(profile.id);
                      return (
                        <label
                          key={profile.id}
                          className={`flex items-start gap-3 p-3 rounded-xl cursor-pointer transition-all ${
                            isAssigned
                              ? isDarkMode
                                ? 'bg-indigo-500/20 border border-indigo-400/30'
                                : 'bg-brand/10 border border-brand/30'
                              : isDarkMode
                                ? 'bg-gray-800/30 border border-white/10 hover:border-white/20'
                                : 'bg-white border border-slate-200 hover:border-slate-300'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 mt-0.5 ${
                            isAssigned
                              ? 'bg-brand text-white'
                              : isDarkMode ? 'bg-gray-700 border border-white/20' : 'bg-slate-100 border border-slate-300'
                          }`}>
                            {isAssigned && <Check className="w-3 h-3" />}
                          </div>
                          <input
                            type="checkbox"
                            checked={isAssigned}
                            onChange={() => {
                              if (isAssigned) {
                                setAssignedProfiles(prev => prev.filter(id => id !== profile.id));
                              } else {
                                setAssignedProfiles(prev => [...prev, profile.id]);
                              }
                            }}
                            className="sr-only"
                          />
                          <div className="flex-1 min-w-0">
                            <div className={`font-medium truncate ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
                              {profile.name}
                            </div>
                            {profile.description && (
                              <div className={`text-xs truncate ${isDarkMode ? 'text-white/50' : 'text-slate-500'}`}>
                                {profile.description}
                              </div>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                )}

                {assignedProfiles.length === 0 && !profilesLoading && (
                  <p className={`text-sm mt-3 ${isDarkMode ? 'text-orange-300' : 'text-orange-600'}`}>
                    {t('admin.userForm.noProfile')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-6">
            {/* Cancel Button */}
            <button
              onClick={onCancel}
              disabled={userLoading}
              className={`relative group/cancel overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:transform-none disabled:opacity-50 ${isDarkMode ? '' : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(71,85,105,0.6) 0%, rgba(75,85,99,0.6) 50%, rgba(71,85,105,0.6) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/cancel:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                } : {}}
              ></div>

              <div className={`relative z-10 px-4 py-2 flex items-center justify-center font-medium transition-all duration-300 ${isDarkMode ? 'text-white drop-shadow-lg group-hover/cancel:drop-shadow-xl' : ''}`}>
                {t('common.cancel')}
              </div>

              <div className={`absolute inset-0 opacity-0 group-hover/cancel:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/cancel:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.1) 50%, transparent 100%)'
                  } : {}}
                ></div>
              </div>
            </button>

            {/* Save Button */}
            <button
              onClick={handleSubmit}
              disabled={userLoading}
              className={`relative group/save overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:transform-none disabled:opacity-50 ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] text-white shadow-sm'}`}
              style={isDarkMode ? {
                background: isEditing
                  ? 'linear-gradient(135deg, rgba(34,197,94,0.7) 0%, rgba(21,128,61,0.7) 50%, rgba(22,101,52,0.7) 100%)'
                  : 'linear-gradient(135deg, rgba(59,130,246,0.7) 0%, rgba(37,99,235,0.7) 50%, rgba(29,78,216,0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: isEditing
                  ? '0 8px 32px rgba(34,197,94,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
                  : '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/save:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                } : {}}
              ></div>

              <div className={`relative z-10 px-4 py-2 flex items-center justify-center font-medium text-white transition-all duration-300 ${isDarkMode ? 'drop-shadow-lg group-hover/save:drop-shadow-xl' : ''}`}>
                {userLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/save:scale-110" />
                    {isEditing ? t('common.update') : t('common.create')}
                  </>
                )}
              </div>

              <div className={`absolute inset-0 opacity-0 group-hover/save:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/save:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  } : {}}
                ></div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserForm;